import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Envelope } from '../../interfaces/envelope';
import { notificationsService } from '../services/notifications.service';
import {
	PostPutSubscriptionsUnsigned,
	SubscriptionsUnsigned,
} from '../../interfaces/notifications';

interface EventsState {
	subscriptionsUnsigned: {
		[id: number]: SubscriptionsUnsigned;
	};
	isLoadingSubscriptionsUnsigned: boolean;
}

const initialState = {
	subscriptionsUnsigned: [],
	isLoadingSubscriptionsUnsigned: false,
} as EventsState;

export const fetchSubscriptionsUnsigned = createAsyncThunk<
	SubscriptionsUnsigned[],
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('notifications/fetchSubscriptionsUnsigned', async (_undefined, { rejectWithValue }) => {
	try {
		return await notificationsService.fetchSubscriptionsUnsigned();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const addSubscriptionsUnsigned = createAsyncThunk<
	SubscriptionsUnsigned[],
	{ params: PostPutSubscriptionsUnsigned },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('notifications/addSubscriptionsUnsigned', async ({ params }, { rejectWithValue }) => {
	try {
		return await notificationsService.addSubscriptionsUnsigned(params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const editSubscriptionsUnsigned = createAsyncThunk<
	{ id: string; subscription: SubscriptionsUnsigned[] },
	{ id: string; params: PostPutSubscriptionsUnsigned },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('notifications/editSubscriptionsUnsigned', async ({ id, params }, { rejectWithValue }) => {
	try {
		const subscription = await notificationsService.editSubscriptionsUnsigned(id, params);
		return {
			id,
			subscription,
		};
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const deleteSubscriptionsUnsigned = createAsyncThunk<
	string,
	{ id: string },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('notifications/deleteSubscriptionsUnsigned', async ({ id }, { rejectWithValue }) => {
	try {
		await notificationsService.deleteSubscriptionsUnsigned(id);
		return id;
	} catch (error) {
		return rejectWithValue(error);
	}
});

const notificationsSlice = createSlice({
	name: 'notifications',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchSubscriptionsUnsigned.fulfilled, (state, action) => {
			const ret: { [id: number]: SubscriptionsUnsigned } = {};
			state.subscriptionsUnsigned = action.payload.reduce((obj, SubscriptionsUnsigned) => {
				obj[SubscriptionsUnsigned.id] = SubscriptionsUnsigned;
				return obj;
			}, ret);
			state.isLoadingSubscriptionsUnsigned = false;
		});
		builder.addCase(fetchSubscriptionsUnsigned.pending, (state) => {
			state.isLoadingSubscriptionsUnsigned = true;
		});
		builder.addCase(fetchSubscriptionsUnsigned.rejected, (state, action) => {
			state.subscriptionsUnsigned = {};
			state.isLoadingSubscriptionsUnsigned = false;
		});

		builder.addCase(addSubscriptionsUnsigned.fulfilled, (state, action) => {
			state.subscriptionsUnsigned[action.payload[0].id] = action.payload[0];
			state.isLoadingSubscriptionsUnsigned = false;
		});
		builder.addCase(addSubscriptionsUnsigned.pending, (state) => {
			state.isLoadingSubscriptionsUnsigned = true;
		});
		builder.addCase(addSubscriptionsUnsigned.rejected, (state, action) => {
			state.isLoadingSubscriptionsUnsigned = false;
		});

		builder.addCase(editSubscriptionsUnsigned.fulfilled, (state, action) => {
			delete state.subscriptionsUnsigned[Number(action.payload.id)];
			state.subscriptionsUnsigned[action.payload.subscription[0].id] =
				action.payload.subscription[0];
			state.isLoadingSubscriptionsUnsigned = false;
		});
		builder.addCase(editSubscriptionsUnsigned.pending, (state) => {
			state.isLoadingSubscriptionsUnsigned = true;
		});
		builder.addCase(editSubscriptionsUnsigned.rejected, (state, action) => {
			state.isLoadingSubscriptionsUnsigned = false;
		});

		builder.addCase(deleteSubscriptionsUnsigned.fulfilled, (state, action) => {
			delete state.subscriptionsUnsigned[Number(action.payload)];
			state.isLoadingSubscriptionsUnsigned = false;
		});
		builder.addCase(deleteSubscriptionsUnsigned.pending, (state) => {
			state.isLoadingSubscriptionsUnsigned = true;
		});
		builder.addCase(deleteSubscriptionsUnsigned.rejected, (state, action) => {
			state.isLoadingSubscriptionsUnsigned = false;
		});
	},
});

export default notificationsSlice;
